import React, { useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "../../Utils/utils";
import "./PaymentVerificationPopup.css";

/**
 * PaymentVerificationPopup
 *
 * Props:
 * - isOpen: boolean
 * - initialSeconds: number
 * - onAutoAction: function
 *
 * Example:
 * <PaymentVerificationPopup
 *   isOpen={true}
 *   initialSeconds={10}
 *   onAutoAction={() => console.log("Call API / verify payment")}
 * />
 */
export default function PaymentVerificationPopup({
    isOpen = true,
    initialSeconds = 10,
    pgMessage = "",
    onAutoAction,
    onButtonPressed,
}) {
    const safeSeconds = useMemo(
        () => Math.max(0, Number(initialSeconds) || 0),
        [initialSeconds]
    );

    const [secondsLeft, setSecondsLeft] = useState(safeSeconds);
    const finishedRef = useRef(false);

    useEffect(() => {
        if (!isOpen) return;
        setSecondsLeft(safeSeconds);
        finishedRef.current = false;
    }, [isOpen, safeSeconds]);

    useEffect(() => {
        if (!isOpen) return;

        if (secondsLeft <= 0) {
            if (!finishedRef.current) {
                finishedRef.current = true;
                onAutoAction?.();
            }
            return;
        }

        const timer = setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isOpen, secondsLeft, onAutoAction]);

    if (!isOpen) return null;

    const hasMessage = !isEmpty(pgMessage);

    return (
        <div className="payment-popup-overlay">
            <div className="payment-popup-card">
                <div className="payment-popup-header">
                    <div>
                        <h2 className="payment-popup-title">Payment Verification</h2>
                        <p className="payment-popup-subtitle">
                            Wait for {secondsLeft} sec, until payment not received.
                        </p>
                    </div>
                </div>

                <div className="payment-popup-timer-wrapper">
                    <div className="payment-popup-timer-box">
                        <div className="payment-popup-timer-content">
                            <div className="payment-popup-seconds">{secondsLeft}</div>
                            <div className="payment-popup-seconds-label">seconds</div>
                        </div>
                    </div>
                </div>

                <div className="payment-popup-status-wrapper">
                    <div className="payment-popup-status-box">
                        {!hasMessage && (
                            <div className="payment-popup-slide-icon">
                                <div className="payment-popup-card-icon">
                                    {/* same svg */}
                                </div>
                            </div>
                        )}

                        <div className="payment-popup-status-content">
                            <div className="payment-popup-status-pill">
                                {!hasMessage && <span className="payment-popup-pulse-dot" />}
                                <span className="payment-popup-status-text">
                                    {hasMessage ? pgMessage : "Verifying payment..."}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {hasMessage && (
                    <div className="payment-popup-button-wrapper">
                        <button
                            type="button"
                            onClick={onButtonPressed}
                            className="payment-popup-button"
                        >
                            Ok!
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}